/**
 * Sentry init for the React app.
 *
 * `initSentry()` is a no-op when `VITE_SENTRY_DSN` is unset (typical in
 * local dev). When the DSN is present, the SDK auto-captures unhandled
 * errors, attaches the current `view` as a tag (kept fresh by
 * `ViewProvider`), and routes performance traces at the project's
 * sample rate.
 *
 * The frontend DSN is **public by design** — Vite inlines it into the
 * bundle. Project-side rate limits and inbound filters protect it; do
 * not treat it as a secret.
 *
 * Adding a new sensitive field? Add it to `SCRUB_KEYS` AND the matching
 * `_SENSITIVE_KEYS` set in `backend/app/lib/sentry.py`.
 */

import * as Sentry from '@sentry/react'

import { env } from './env'

const SCRUB_KEYS = [
  'card_number',
  'cardnumber',
  'card',
  'cvv',
  'cvc',
  'password',
  'passwd',
  'secret',
  'token',
  'jwt',
  'authorization',
  'phone',
  'phone_number',
  'email',
  'email_address',
  'medical_history',
  'medical_records',
  'passport',
  'passport_number',
  // M40: the Transport booking's private-link key, sent as a header.
  'x-transport-key',
] as const

const SCRUBBED = '<scrubbed>'

/**
 * M40: a Transport booking's private link carries its key in the URL
 * fragment (`/transport/booking/TRN-…#k=<key>`). The SDK reports the page's
 * full URL (`request.url`, breadcrumbs, a transaction's name), fragment
 * included, so the key is cut out of every string, wherever it sits.
 */
const LINK_KEY = /([#&?]k=)[^&#\s"']+/g
/** The same, percent-encoded (`%23k%3D…`): inside another URL's parameter. */
const ENCODED_LINK_KEY = /(%23k%3D)[^&#%\s"']+/gi

/** The SDK's own bookkeeping (live Scope and client objects); the envelope
 * drops it before sending, and it must never be walked or copied. */
const SDK_INTERNAL = 'sdkProcessingMetadata'

/** Only plain data is walked: a Map, a Date or a class instance is left as
 * it is, so nothing the SDK holds is copied or turned into `{}`. */
function isPlain(value: object): boolean {
  const proto: unknown = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

function scrub(value: unknown): unknown {
  if (typeof value === 'string')
    return value
      .replace(LINK_KEY, `$1${SCRUBBED}`)
      .replace(ENCODED_LINK_KEY, `$1${SCRUBBED}`)
  if (Array.isArray(value)) return value.map(scrub)
  if (value !== null && typeof value === 'object' && isPlain(value)) {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] =
        k === SDK_INTERNAL
          ? v
          : SCRUB_KEYS.includes(k.toLowerCase() as (typeof SCRUB_KEYS)[number])
            ? SCRUBBED
            : scrub(v)
    }
    return out
  }
  return value
}

export function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  const scrubbed = scrub(event)
  return scrubbed && typeof scrubbed === 'object'
    ? (scrubbed as Sentry.ErrorEvent)
    : event
}

/** The event `beforeSendTransaction` receives (not exported by name). */
export type TransactionEvent = Parameters<
  NonNullable<Sentry.BrowserOptions['beforeSendTransaction']>
>[0]

/** Performance transactions skip `beforeSend`; they carry page URLs too. */
export function beforeSendTransaction(
  event: TransactionEvent,
): TransactionEvent | null {
  const scrubbed = scrub(event)
  return scrubbed && typeof scrubbed === 'object'
    ? (scrubbed as TransactionEvent)
    : event
}

export function initSentry(): void {
  if (!env.sentryDsn) {
    return
  }
  Sentry.init({
    dsn: env.sentryDsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    beforeSend,
    beforeSendTransaction,
  })
}

export { Sentry }
